import { cn } from "../lib/utils";
import React from "react";
import { Icon } from "./Icon";
import { ListItem } from "./ListItem";
/**
 * A rich list row for representing a resource, connection, or agent, with a
 * leading visual, a title, optional subElement metadata, free-form children,
 * and a trailing action. Use it to list connected platforms, data sources,
 * agents, or settings entries, composing rows inside ContextItem.List; for a
 * denser settings layout with a single control, prefer SettingsList.Row.
 * @summary Rich list row with visual, metadata, and action.
 */
export function ContextItem({ action, children, className, hasSeparator = true, hasSeparatorIfLast = false, subElement, title, visual, hoverAction, onClick, truncateSubElement, }) {
    return (React.createElement(ListItem, { className: className, onClick: onClick, hasSeparator: hasSeparator, hasSeparatorIfLast: hasSeparatorIfLast, groupName: "context-item", itemsAlignment: children ? "start" : "center" },
        visual,
        React.createElement("div", { className: "mb-0.5 flex min-w-0 grow flex-col gap-0" },
            React.createElement("div", { className: "flex min-w-0 grow flex-col text-foreground sm:flex-row sm:gap-3" },
                React.createElement("div", { className: cn("heading-base", truncateSubElement
                        ? "shrink-0"
                        : "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap") }, title),
                subElement &&
                    (truncateSubElement ? (React.createElement("div", { className: "flex min-w-0 items-center gap-3 overflow-hidden text-sm text-muted-foreground" },
                        React.createElement("div", { className: "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" }, subElement))) : (React.createElement("div", { className: "flex items-center gap-3 overflow-hidden text-sm text-muted-foreground" }, subElement)))),
            children && React.createElement("div", null, children)),
        React.createElement("div", { className: cn(hoverAction &&
                "opacity-0 transition-opacity duration-200 group-hover/context-item:opacity-100") }, action)));
}
/** Vertical list container for ContextItem rows and ContextItem.SectionHeader groups. */
ContextItem.List = function ({ children, className, hasBorder, }) {
    // Ensure all children are of type ContextItem or ContextItem.SectionHeader
    React.Children.forEach(children, (child) => {
        if (child === null || child === undefined) {
            return;
        }
        if (!React.isValidElement(child) ||
            (child.type !== ContextItem &&
                child.type !== ContextItem.SectionHeader &&
                // all children of child must be of type ContextItem or ContextItem.SectionHeader
                React.Children.toArray(child.props.children).some((c) => !React.isValidElement(c) ||
                    (c.type !== ContextItem && c.type !== ContextItem.SectionHeader)))) {
            throw new Error("All children of ContextItem.List must be of type ContextItem or ContextItem.SectionHeader");
        }
    });
    return (React.createElement("div", { className: cn("flex flex-col", className, hasBorder && "border-b border-t border-border") }, children));
};
/** Muted description text rendered inside a ContextItem row. */
ContextItem.Description = function ({ children, description, }) {
    return (React.createElement(React.Fragment, null,
        description && (React.createElement("div", { className: "text-sm font-normal text-muted-foreground" }, description)),
        children && React.createElement(React.Fragment, null, children)));
};
/** Standard-size icon/logo wrapper for a ContextItem row's leading visual. */
ContextItem.Visual = function ({ visual }) {
    return React.createElement(Icon, { size: "md", visual: visual });
};
/** Titled section header used to break a ContextItem.List into groups. */
ContextItem.SectionHeader = function ({ title, description, hasBorder = true, }) {
    return (React.createElement("div", { className: cn("flex flex-col gap-0 pb-3 pt-7", hasBorder && "border-b border-border") },
        React.createElement("div", { className: "heading-xl text-foreground" }, title),
        description && (React.createElement("div", { className: "copy-sm text-muted-foreground" }, description))));
};
//# sourceMappingURL=ContextItem.js.map